import { useEffect, useState, type TransitionEvent } from "react";
import { PawPrintIcon } from "lucide-react";

import { StafferPetProfileSprite } from "../staffer-pets/StafferPetProfileSprite";
import { Column, Row } from "@/components";
import { racetrackLanes } from "@/lib/racetrackLanes";

import type { PetAppearance } from "@/types";

/* Constants */

const DUNE_WORM_HEIGHT_PX = 70;
const NO_PET_ICON_SIZE_PX = 28;

// One soft shadow keeps the sprite visible against the dark track without
// stacking filters around animated rainbow sprites.
const TRACK_PET_SHADOW_CLASS =
  "overflow-visible [filter:drop-shadow(0_1px_3px_rgba(255,255,255,0.18))]";

// Racers start at the start gate on mount, then run forward to their position.
const ENTRY_START_PERCENT = 0;

/* Render */

export const RacetrackPetRacer = ({
  progressPercent,
  laneZIndex = 1,
  pet,
  sizePx = racetrackLanes.PET_HEIGHT_PX,
  maxHeightPx,
  isEntryAnimated = true,
}: {
  // 0–100 position along the lane track.
  progressPercent: number;
  // Higher lanes (lower on screen) stack in front of earlier lanes.
  laneZIndex?: number;
  pet?: PetAppearance;
  sizePx?: number;
  maxHeightPx?: number;
  // Read on mount only: racers added after the track's entry start in place.
  isEntryAnimated?: boolean;
}) => {
  /* React Hooks */
  const [hasEntered, setHasEntered] = useState(!isEntryAnimated);
  const [isRunningEntry, setIsRunningEntry] = useState(isEntryAnimated);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (hasEntered) return;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(motionQuery.matches);

    if (motionQuery.matches) {
      setHasEntered(true);
      setIsRunningEntry(false);
      return;
    }

    // Double rAF: lets the browser paint the start-gate frame before we flip to
    // the target, so the run-forward transition actually runs (not a jump).
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        setHasEntered(true);
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [hasEntered]);

  /* Helpers */
  const preferredPetHeightPx =
    pet?.equipped.petId === "DUNE_WORM"
      ? DUNE_WORM_HEIGHT_PX * (sizePx / racetrackLanes.PET_HEIGHT_PX)
      : sizePx;
  const petHeightPx =
    maxHeightPx === undefined
      ? preferredPetHeightPx
      : Math.min(preferredPetHeightPx, maxHeightPx);
  const preferredNoPetIconSizePx =
    NO_PET_ICON_SIZE_PX * (sizePx / racetrackLanes.PET_HEIGHT_PX);
  const noPetIconSizePx =
    maxHeightPx === undefined
      ? preferredNoPetIconSizePx
      : Math.min(preferredNoPetIconSizePx, maxHeightPx);
  const displayProgress =
    prefersReducedMotion || hasEntered ? progressPercent : ENTRY_START_PERCENT;
  // Only the entry run animates; later revenue changes move racers instantly.
  const hasEntryTransition = isRunningEntry && !prefersReducedMotion;

  /* Handlers */
  const handleEntryEnd = (event: TransitionEvent<HTMLDivElement>) => {
    // Ignore transitions bubbling up from the sprite.
    if (event.target !== event.currentTarget) return;
    setIsRunningEntry(false);
  };

  /* Render */
  const renderPet = () => {
    if (!pet) {
      return (
        <PawPrintIcon
          className="text-white/50"
          style={{ width: noPetIconSizePx, height: noPetIconSizePx }}
        />
      );
    }

    return (
      <StafferPetProfileSprite pet={pet} sizePx={petHeightPx} isOnLeaderboard />
    );
  };

  // Animate transform, not `left`, so the run-in skips layout every frame.
  // The lane wrapper is a size container: 1cqw = 1% of the lane's width.
  return (
    <Column
      className="pointer-events-none absolute inset-0 [container-type:inline-size]"
      style={{ zIndex: laneZIndex }}
    >
      <Column
        className="pointer-events-auto absolute left-0 top-1/2 w-max"
        style={{
          transform: `translate(calc(${displayProgress.toString()}cqw - 100%), -50%)`,
          transition: hasEntryTransition
            ? `transform ${racetrackLanes.ENTRY_DURATION_MS.toString()}ms linear`
            : undefined,
        }}
        onTransitionEnd={handleEntryEnd}
      >
        <Row className={TRACK_PET_SHADOW_CLASS}>{renderPet()}</Row>
      </Column>
    </Column>
  );
};
